import PropTypes from 'prop-types';
import { Icon } from '@eti/components';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { constants, price as priceUtils, traveler as travelerUtils } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { resetButtonStyles } from '../utils/styles';
import { EMPTY_FIELDS_STRING, TRAVELER_ACTIVE_BUTTON_MIN_WIDTH } from '../utils/constants';
import TravelerIcon from './TravelerIcon';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const getMinWidth = navigationAvailable => navigationAvailable ? `calc(100vw - 143px)` : `${TRAVELER_ACTIVE_BUTTON_MIN_WIDTH}px`;
const listItemStyles = (active, navigationAvailable) => css`
  flex-grow: ${active ? 1 : 0};
  max-width: 450px;
  min-width: ${active ? getMinWidth(navigationAvailable) : '1px'};
  ${active && 'transition: 0.4s ease-in-out'};
  position: relative;

  @media ${mediaQueries.large.up} {
    min-width: ${active ? '350px' : '1px'};
    ${active && 'transition: 0.3s ease-in-out'};
  }
`;
const travelerStyles = active => css`
  ${resetButtonStyles}; /* stylelint-disable-next-line */
  align-items: center;
  background-color: ${active ? etiColors.lightBlue : 'transparent'};
  cursor: ${active ? 'default' : 'pointer'};
  display: flex;
  flex-direction: row;
  flex-grow: ${active ? 1 : 0};
  padding: 4px 0;
  width: 100%;

  &:focus {
    box-shadow: 0 0 5px rgba(53 134 209 / 50%);
    outline: 1px solid ${etiColors.info.solid};

    &::-moz-focus-inner {
      border: 0;
    }
  }
`;
const travelerInformationStyles = active => css`
  display: ${active ? 'flex' : 'none'};
  flex-direction: column;
  font-size: 0.625rem;
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  width: 100%;
`;
const travelerTypeIconWrapperStyles = css`
  position: relative;
`;
const selectedSeatLabelStyles = css`
  margin-right: 4px;

  html[dir='rtl'] & {
    margin-left: 4px;
  }
`;
const selectedSeatStyles = selectedSeat => css`
  color: ${selectedSeat ? etiColors.confirmation.solid : '#000'};
`;
const travelerDescriptionStyles = css`
  align-items: baseline;
  display: flex;
  flex-direction: row;
  left: 0;
  position: absolute;
  top: 0;
  width: calc(100% - 1rem);
`;
const travelerNameStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const travelerTypeStyles = css`
  margin-left: 0.25rem;
  white-space: nowrap;
`;
const selectedSeatValueStyles = css`
  font-weight: 700;
`;
const removeSelectionButtonStyles = css`
  color: ${etiColors.info.solid};
  cursor: pointer;
  margin-left: 5px;
`;
const travelerStatusIconStyles = active => css`
  bottom: 0.35rem;
  fill: ${active ? etiColors.info.washed : etiColors.confirmation.solid};
  height: 0.75rem;
  position: absolute;
  right: 0.45rem;
  transform: ${active ? 'rotate(90deg)' : null};
  width: 0.75rem;
`;
const seatStyles = css`
  margin-top: 0.875rem;
`;
const SeatMapTraveler = _ref => {
  let {
    active = [],
    ageType,
    gender,
    id,
    initials,
    name,
    isNavigationAvailable,
    onChange = () => {},
    onRemoveSeat,
    selectedSeats = [],
    travelerIndex
  } = _ref;
  const {
    t
  } = useTranslation();
  const priceLabel = t('Product.SeatMap.Seat.Price');
  const selectedSeatLabel = t('Product.SeatMap.Seat');
  const removeSeatText = t('Product.SeatMap.Summary.Seat.Remove');
  const adultLabel = t('Adult');
  const childLabel = t('Child');
  const infantLabel = t('Infant');
  const isActive = active.length === 2 && id === active[1];
  const selectedSeat = selectedSeats.find(seat => String(seat.id) === String(id));
  const typeLabel = travelerUtils.resolveLabelFromType(ageType, adultLabel, childLabel, infantLabel);
  return /*#__PURE__*/_jsx("li", {
    className: listItemStyles(isActive, isNavigationAvailable),
    "data-testid": `seatMapTraveler-${id}`,
    children: /*#__PURE__*/_jsxs("button", {
      className: cx(travelerStyles(isActive), 'seatMap__traveler'),
      onClick: onChange(id),
      type: "button",
      children: [/*#__PURE__*/_jsxs("div", {
        className: travelerTypeIconWrapperStyles,
        "data-testid": `seatMap-travelerTypeIcon-${selectedSeat ? `initials-${initials}` : ageType}`,
        children: [/*#__PURE__*/_jsx(TravelerIcon, {
          ageType: ageType,
          className: "seatMap__travelerTypeIcon",
          gender: gender,
          initials: initials
        }), selectedSeat && /*#__PURE__*/_jsx(Icon, {
          className: cx(travelerStatusIconStyles(!selectedSeat && isActive), 'seatMap__travelerStatusIcon'),
          id: "check"
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: cx(travelerInformationStyles(isActive), 'seatMap__travelerInfo'),
        children: [/*#__PURE__*/_jsxs("span", {
          className: travelerDescriptionStyles,
          "data-testid": "seatMap-travelerInformation",
          children: [/*#__PURE__*/_jsx("span", {
            className: cx('seatMap__travelerName', travelerNameStyles, MOUSE_FLOW_CLASS_NAMES),
            children: `${name},`
          }), /*#__PURE__*/_jsx("span", {
            className: cx('seatMap__travelerType', travelerTypeStyles),
            children: typeLabel
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: cx('seatMap__selectedSeat', seatStyles),
          children: [/*#__PURE__*/_jsx("span", {
            className: selectedSeatLabelStyles,
            "data-testid": `seatMapTraveler-${travelerIndex}-selectedSeatLabel`,
            children: selectedSeatLabel
          }), /*#__PURE__*/_jsx("span", {
            className: selectedSeatStyles(selectedSeat),
            "data-testid": `seatMapTraveler-${travelerIndex}-selectedSeat`,
            children: selectedSeat ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                className: selectedSeatValueStyles,
                children: selectedSeat.value
              }), /*#__PURE__*/_jsx("span", {
                className: cx(removeSelectionButtonStyles, 'seatMap__removeSelection'),
                "data-testid": `seatMapTraveler-${travelerIndex}-removeSelection-button`,
                onClick: onRemoveSeat,
                children: removeSeatText
              })]
            }) : EMPTY_FIELDS_STRING
          })]
        }), /*#__PURE__*/_jsxs("span", {
          className: "seatMap__travelerPrice",
          "data-testid": `seatMap-priceInformation-${travelerIndex}`,
          children: [priceLabel, ' ', selectedSeat ? priceUtils.formatPrice(selectedSeat.price) : EMPTY_FIELDS_STRING]
        })]
      })]
    })
  });
};
SeatMapTraveler.propTypes = {
  active: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  ageType: PropTypes.string,
  gender: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initials: PropTypes.string,
  isNavigationAvailable: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onRemoveSeat: PropTypes.func,
  selectedSeats: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.any), PropTypes.arrayOf(PropTypes.any)]),
  travelerIndex: PropTypes.number
};
export default SeatMapTraveler;